/**
 * Lib.Common: Dates and Times
 */


import $locale from './locale';


const re_date = /^([+-]?\d{4}(?!\d\d\b))(?:-?(?:(0[1-9]|1[0-2])(?:-?([12]\d|0[1-9]|3[01]))?)(?:[T\s](?:(?:([01]\d|2[0-3])(?::?([0-5]\d))?|24\:?00)([.,]\d+(?!:))?)?(?::?([0-5]\d)(?:[.,](\d+))?)?([zZ]|([+-](?:[01]\d|2[0-3])):?([0-5]\d)?)?)?)?$/;
const re_diff = /^([+-]?)P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)W)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?$/;


const pad = n => n < 10 ? '0' + n : n;

const unitSecond  = 1000;
const unitMinute  = 1000 * 60;
const unitHour    = 1000 * 60 * 60;
const unitDay     = 1000 * 60 * 60 * 24;
const unitYear    = 1000 * 60 * 60 * 24 * 365;

const emDash = '—';


let _now;
const now = () => {
    if (!_now) {
        _now = Date.now();
        setTimeout(() => { _now = 0; }, 1000);
    }
    return _now;
};

export const parse = (when, utc) => {
    const match = when.match(re_date);
    if (match) {
        const value = new Date(match[1], match[2] - 1 | 0, match[3] || 1, match[4] | 0, match[5] | 0, match[7] | 0, match[8] | 0);
        if (match[9] || utc) {
            value.setUTCMinutes(value.getUTCMinutes() - value.getTimezoneOffset() - ((match[10] * 60 + +match[11]) || 0));
        }
        return value;
    } else {
        return new Date(when);
    }
};


const prototype = {
    add(diff) {
        const g = diff.match(re_diff);
        if (!g) {
            throw new RangeError('Invalid duration');
        }
        const sign = g[1] + 1 | 0;
        if (g[2]) {
            this.year += sign * g[2];
        }
        if (g[3]) {
            this.month += sign * g[3];
        }
        if (g[4]) {
            this.day += sign * g[4] * 7;
        }
        if (g[5]) {
            this.day += sign * g[5];
        }
        if (g[6]) {
            this.hour += sign * g[6];
        }
        if (g[7]) {
            this.minute += sign * g[7];
        }
        if (g[8]) {
            this.second += sign * g[8];
        }
        return this;
    },

    valueOf() {
        return +this.value;
    },

    unix() {
        return this.value / 1000 | 0;
    },

    isValid() {
        return !isNaN(this.value);
    },

    isEqual(other) {
        return +this.value == +other;
    },

    diffSeconds(other) {
        return (this.value - other) / unitSecond | 0;
    },

    diffMinutes(other) {
        return (this.value - other) / unitMinute | 0;
    },

    diffHours(other) {
        return (this.value - other) / unitHour | 0;
    },

    diffDays(other) {
        return (this.value - other) / unitDay | 0;
    },

    previousYear() {
        this.year--;
        return this;
    },

    previousMonth() {
        this.month--;
        return this;
    },

    previousDay() {
        this.day--;
        return this;
    },

    nextYear() {
        this.year++;
        return this;
    },

    nextMonth() {
        this.month++;
        return this;
    },

    nextDay() {
        this.day++;
        return this;
    },

    startOfYear() {
        this.month = 0;
        this.day = 1;
        return this.startOfDay();
    },

    startOfMonth() {
        this.day = 1;
        return this.startOfDay();
    },

    startOfWeek() {
        this.day -= this.getDayOfWeek();
        return this.startOfDay();
    },

    startOfDay() {
        this.hour = this.minute = this.second = this.millisecond = 0;
        return this;
    },

    endOfYear() {
        this.year++;
        this.month = 0;
        this.day = 0;
        return this.endOfDay();
    },

    endOfMonth() {
        this.month++;
        this.day = 0;
        return this.endOfDay();
    },

    endOfWeek() {
        this.day += 6 - this.getDayOfWeek();
        return this.endOfDay();
    },

    endOfDay() {
        this.hour = 23;
        this.minute = 59;
        this.second = 59;
        this.millisecond = 999;
        return this;
    },

    toUTCString() {
        return this.value.toISOString();
    },

    __date: true
};


/**
 * Date stored as UTC but parsed by default as local time, and with arithmetic and string formatting in local time.
 */

let $dateLocal = function(when) {
    this.value = typeof when === 'string'
        ? parse(when)
        : new Date(when === void 0 ? now() : when == null ? NaN : +when);
};


$dateLocal.prototype = {
    clone() {
        return new $dateLocal(this);
    },

    toUTC() {
        return new $dateUTC(this);
    },

    isLocal() {
        return true;
    },

    getDayOfWeek() {
        return (this.value.getDay() + 6) % 7;
    },

    getOffset() {
        return this.value.getTimezoneOffset();
    },

    /**
     * Retrieve ISO 8601 representation of date and time, in local time zone.
     */
    toString() {
        const value = this.value;
        let off = value.getTimezoneOffset();
        const sign = off < 0 ? '+' : '-';
        off = Math.abs(off);
        return isNaN(+this.value)
            ? void 0
            : this.toDateString() + 'T' +
                pad(value.getHours()) + ':' +
                pad(value.getMinutes()) + ':' +
                pad(value.getSeconds()) + sign +
                pad(off / 60 | 0) +
                pad(off % 60);
    },

    /**
     * Retrieve ISO 8601 representation of date only, in local time zone.
     */
    toDateString() {
        const value = this.value;
        return isNaN(+this.value)
            ? void 0
            : value.getFullYear() + '-' +
                pad(value.getMonth()+1) + '-' +
                pad(value.getDate());
    },

    ...prototype
};

Object.defineProperty($dateLocal.prototype, 'millisecond', {
    get() {
        return this.value.getMilliseconds();
    },
    set(value) {
        this.value.setMilliseconds(value);
    }
});

Object.defineProperty($dateLocal.prototype, 'second', {
    get() {
        return this.value.getSeconds();
    },
    set(value) {
        this.value.setSeconds(value);
    }
});

Object.defineProperty($dateLocal.prototype, 'minute', {
    get() {
        return this.value.getMinutes();
    },
    set(value) {
        this.value.setMinutes(value);
    }
});

Object.defineProperty($dateLocal.prototype, 'hour', {
    get() {
        return this.value.getHours();
    },
    set(value) {
        this.value.setHours(value);
    }
});

Object.defineProperty($dateLocal.prototype, 'day', {
    get() {
        return this.value.getDate();
    },
    set(value) {
        this.value.setDate(value);
    }
});

Object.defineProperty($dateLocal.prototype, 'month', {
    get() {
        return this.value.getMonth();
    },
    set(value) {
        this.value.setMonth(value);
    }
});

Object.defineProperty($dateLocal.prototype, 'year', {
    get() {
        return this.value.getFullYear();
    },
    set(value) {
        this.value.setFullYear(value);
    }
});


/**
 * Date stored as UTC and parsed by default as UTC, with arithmetic and string formatting in UTC time.
 */

let $dateUTC = function(when) {
    this.value = typeof when === 'string'
        ? parse(when, true)
        : new Date(when === void 0 ? now() : when == null ? NaN : +when);
};


$dateUTC.prototype = {
    clone() {
        return new $dateUTC(this);
    },

    toLocal() {
        return new $dateLocal(this);
    },

    isLocal() {
        return false;
    },

    getDayOfWeek() {
        return (this.value.getUTCDay() + 6) % 7;
    },

    getOffset() {
        return 0;
    },

    /**
     * Retrieve ISO 8601 representation of date and time, adjusted to UTC.
     */
    toString() {
        return isNaN(+this.value)
            ? void 0
            : this.value.toISOString();
    },

    /**
     * Retrieve ISO 8601 representation of date only, adjusted to UTC.
     */
    toDateString() {
        const value = this.value;
        return isNaN(+value)
            ? void 0
            : value.getUTCFullYear() + '-' +
                pad(value.getUTCMonth()+1) + '-' +
                pad(value.getUTCDate());
    },

    ...prototype
};

Object.defineProperty($dateUTC.prototype, 'millisecond', {
    get() {
        return this.value.getUTCMilliseconds();
    },
    set(value) {
        this.value.setUTCMilliseconds(value);
    }
});

Object.defineProperty($dateUTC.prototype, 'second', {
    get() {
        return this.value.getUTCSeconds();
    },
    set(value) {
        this.value.setUTCSeconds(value);
    }
});

Object.defineProperty($dateUTC.prototype, 'minute', {
    get() {
        return this.value.getUTCMinutes();
    },
    set(value) {
        this.value.setUTCMinutes(value);
    }
});

Object.defineProperty($dateUTC.prototype, 'hour', {
    get() {
        return this.value.getUTCHours();
    },
    set(value) {
        this.value.setUTCHours(value);
    }
});

Object.defineProperty($dateUTC.prototype, 'day', {
    get() {
        return this.value.getUTCDate();
    },
    set(value) {
        this.value.setUTCDate(value);
    }
});

Object.defineProperty($dateUTC.prototype, 'month', {
    get() {
        return this.value.getUTCMonth();
    },
    set(value) {
        this.value.setUTCMonth(value);
    }
});

Object.defineProperty($dateUTC.prototype, 'year', {
    get() {
        return this.value.getUTCFullYear();
    },
    set(value) {
        this.value.setUTCFullYear(value);
    }
});


const $dateFormat = function(date) {
    if (typeof date === 'string') {
        date = parse(date);
    }
    return !date || isNaN(+date) ? emDash : this.format(+date);
}


const $dateFormatISO = function(date) {
    return date.__date === true
        ? date.toString()
        : new $dateLocal(date).toString();
}


const $dateFormatISODate = function(date) {
    return date.__date === true
        ? date.toDateString()
        : new $dateLocal(date).toDateString();
}


/**
 * Conditional date formatting depending when the date occurred.
 */
const $dateCond = function(date) {
    if (typeof date === 'string') {
        date = parse(date);
    }
    if (!date || isNaN(+date)) {
        return emDash;
    }

    const base = now();
    const diff = base - date + $date.offset;
    return (
        this.future       && diff < 0                       ? this.future :
        this.justNow      && diff < unitSecond * 45         ? this.justNow :
        this.pastMinute   && diff < unitMinute              ? this.pastMinute :
        this.pastHour     && diff < unitHour                ? this.pastHour :
        this.today        && diff < unitHour * 25 && diff >= 0 &&
            +$date.local(date).startOfDay() ===
            +$date.local(base).startOfDay()                 ? this.today :
        this.yesterday    && diff < unitHour * 49 && diff >= 0 &&
            +$date.local(date).startOfDay() ===
            +$date.local(base).previousDay().startOfDay()   ? this.yesterday :
        this.pastDay      && diff < unitDay                 ? this.pastDay :
        this.thisYear     && diff < unitDay * 366 && diff >= 0 &&
            +$date.local(date).startOfYear() ===
            +$date.local(base).startOfYear()                ? this.thisYear :
        this.lastYear     && diff < unitDay * 731 && diff >= 0 &&
            +$date.local(date).startOfYear() ===
            +$date.local(base).previousYear().startOfYear() ? this.lastYear :
        this.pastYear     && diff < unitYear                ? this.pastYear :
        this.other)(date);
}


/**
 * $date: Wrapper for JavaScript `Date` object supporting arithmetic.
 * Instantiate with `$date.local(value)` or `$date.utc(value)`, where value is a number containing
 * milliseconds since the epoch, or an ISO 8601 date/time string.
 */
const $date = {
    /**
     * Offset is used in `ago` or `cond` formatting and should be the time difference between server and user agent time in UTC milliseconds
     */
    offset: 0,
    now,
    utc: when => new $dateUTC(when),
    local: when => new $dateLocal(when),

    /**
     * Convert a date ($date object, numeric, or ISO format) to a locale formatted string.
     *   `$date.iso`:             ISO 8601 date and time format (2017-06-28T13:06:24-0400).
     *   `$date.isodate`:         ISO 8601 date format (2017-06-28).
     *   `$date.hm`:              Hours and minutes in numeric format (1:06 PM or 13:06).
     *   `$date.hms`:             Hours, minutes, and seconds in numeric format (1:06:24 PM or 13:06:24).
     *   `$date.hmsz`:            Hours, minutes, and seconds in numeric format plus tz (1:06:24 PM EDT or 13:06:24 EDT).
     *   `$date.hhmm`:            Hours and minutes in 2-digit format (01:06 PM or 13:06).
     *   `$date.hhmmss`:          Hours, minutes, and seconds in 2-digit format (01:06:24 PM or 13:06:24).
     *   `$date.hhmmssz`:         Hours, minutes, and seconds in 2-digit format plus tz (01:06:24 PM EDT or 13:06:24 EDT).
     *   `$date.m`:               Month in numeric format (6).
     *   `$date.d`:               Day in numeric format (28).
     *   `$date.y`:               Year in numeric format (2017).
     *   `$date.mdyhm`:           Month, day, year, hour, minute in numeric format (6/28/2017, 1:06 PM).
     *   `$date.mdyhms`:          Month, day, year, hour, minute, second in numeric format (6/28/2017, 1:06:24 PM).
     *   `$date.mdyhmsz`:         Month, day, year, hour, minute, second in numeric format plus tz (6/28/2017, 1:06:24 PM EDT).
     *   `$date.mdy`:             Month, day, and year in numeric format (6/28/2017 or 28/6/2017).
     *   `$date.mm`:              Month in 2-digit format (06).
     *   `$date.dd`:              Day in 2-digit format (28).
     *   `$date.yy`:              Year in 2-digit format (17).
     *   `$date.mmdd`:            Month and day in 2-digit format (06/28 or 28/06).
     *   `$date.mmddyy`:          Month, day, and year in 2-digit format (06/28/2017 or 28/06/2017).
     *   `$date.mmm`:             Month in short format (Jun).
     *   `$date.mmmd`:            Month and day in short/numeric format (Jun 28 or 28 Jun).
     *   `$date.mmmy`:            Month and year in short/numeric format (Jun 2017).
     *   `$date.mmmdy`:           Month, day, and year in short/numeric format (Jun 28, 2017 or 28 Jun 2017).
     *   `$date.mmmm`:            Month in long format (June).
     *   `$date.mmmmd`:           Month and day in long/numeric format (June 28 or 28 June).
     *   `$date.mmmmy`:           Month and year in long/numeric format (June 2017).
     *   `$date.mmmmdhm`:         Month, day, hour, and minute in long/numeric format (June 28, 1:06 PM or 28 June, 13:06).
     *   `$date.mmmmdy`:          Month, day, and year in long/numeric format (June 28, 2017 or 28 June 2017).
     *   `$date.wmmmmdy`:         Weekday, month, day, and year (Wednesday, June 28, 2017 or Wednesday, 28 June 2017).
     *   `$date.short`:           Date and time in short format (June 28, 2017, 1:06 PM).
     *   `$date.long`:            Date and time in long format (Wednesday, June 28, 2017, 1:06 PM).
     *
     * To use one of the shortcut methods, you can simply put in your template something like:
     *   `div {{ $date.mmddyy(value) }}`
     */

    _use(language) {
        $date.iso = $dateFormatISO;
        $date.isodate = $dateFormatISODate;

        /**
         * $date.conditional sample usage:
         *     methods: {
         *         myDateFormat: $date.conditional({
         *             today: $date.hm,       // 11:56 PM or 23:56
         *             other: $date.short     // Jun 26, 2019 11:56 PM or 26 Jun 2019, 23:56
         *         })
         *     }
         */
        $date.conditional = options => $dateCond.bind(options);

        const formatter = $date.formatter = options => $dateFormat.bind(new Intl.DateTimeFormat(language, {
            numberingSystem: 'latn',
            calendar: 'gregory',
            ...options
        }));

        const numeric = 'numeric';
        const digit = '2-digit';
        const long = 'long';
        const short = 'short';
        const base = [
            ['hm',,,,,numeric,numeric],
            ['hms',,,,,numeric,numeric,numeric],
            ['hmsz',,,,,numeric,numeric,numeric,short],
            ['hhmm',,,,,digit,digit],
            ['hhmmss',,,,,digit,digit,digit],
            ['hhmmssz',,,,,digit,digit,digit,short],
            ['m',,,numeric],
            ['d',,numeric],
            ['y',,,,numeric],
            ['mdy'],
            ['mdyhm',,numeric,numeric,numeric,numeric,numeric],
            ['mdyhms',,numeric,numeric,numeric,numeric,numeric,numeric],
            ['mdyhmsz',,numeric,numeric,numeric,numeric,numeric,numeric,short],
            ['mm',,,digit],
            ['dd',,digit],
            ['yy',,,,digit],
            ['mmdd',,digit,digit],
            ['mmddyy',,digit,digit,numeric],
            ['mmm',,,short],
            ['mmmd',,numeric,short],
            ['mmmy',,,short,numeric],
            ['mmmdy',,numeric,short,numeric],
            ['mmmm',,,long],
            ['mmmmd',,numeric,long],
            ['mmmmy',,,long,numeric],
            ['mmmmdhm',,numeric,long,,numeric,numeric],
            ['mmmmdy',,numeric,long,numeric],
            ['wmmmmdy',long,numeric,long,numeric],
            ['short',,numeric,long,numeric,numeric,numeric],
            ['long',long,numeric,long,numeric,numeric,numeric]
        ];

        base.forEach(mode => {
            $date[mode[0]] = formatter({
                weekday: mode[1],
                day: mode[2],
                month: mode[3],
                year: mode[4],
                hour: mode[5],
                minute: mode[6],
                second: mode[7],
                timeZoneName: mode[8]
            });
        });

        const ld = $locale.data;

        $date.ago = $dateCond.bind({
            justNow: () => ld.dateNow,
            pastHour: date => {
                let t = Math.round((now() - date + $date.offset) / unitMinute);
                return ld.dateMins.replace('{t}', t);
            },
            pastDay: date => {
                let t = Math.round((now() - date + $date.offset) / unitHour);
                return (t == 1 ? ld.dateHr : ld.dateHrs).replace('{t}', t);
            },
            pastYear: $date.mmmd,
            other: $date.mmmdy
        });

        $date.agoLong = $dateCond.bind({
            justNow: () => ld.dateNow,
            pastHour: date => {
                let t = Math.round((now() - date + $date.offset) / unitMinute);
                return (t == 1 ? ld.dateMinute : ld.dateMinutes).replace('{t}', t);
            },
            pastDay: date => {
                let t = Math.round((now() - date + $date.offset) / unitHour);
                return (t == 1 ? ld.dateHour : ld.dateHours).replace('{t}', t);
            },
            pastYear: $date.mmmmd,
            other: $date.mmmmdy
        });

        // Temporary - backward compatibility
        $date.format = formatter;
        formatter.ago = $date.ago;
        formatter.agoLong = $date.agoLong;
    }
};


export default $date;
